import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ArrowLeft,
  ArrowRight,
  Edit2,
  Eye,
  EyeOff,
  Play,
  Star,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  createEducation,
  deleteEducation,
  getAllEducation,
  updateEducation,
} from "../../../services/education.service";
import { ModalComponent } from "../../../ui-components/Modal";
import SectionTitle from "../../../ui-components/SectionTitle";
import {
  adminButtonOutlinedSx,
  adminButtonPrimarySx,
} from "../../AdminDashboard/modalStyle";

function youtubeId(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.replace("/", "");
    if (u.searchParams.get("v")) return String(u.searchParams.get("v"));
    const parts = u.pathname.split("/");
    const idx = parts.findIndex((p) => p === "embed");
    if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    return "";
  } catch {
    return "";
  }
}

export default function EducationAdmin() {
  const [items, setItems] = useState<any[]>([]);
  const [active, setActive] = useState<any | null>(null);
  const [form, setForm] = useState({
    title: "",
    youtube_url: "",
    description: "",
    category: "Geral",
    thumbnail_url: "",
    published: true,
    order: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Atualiza thumbnail automaticamente a partir da URL do YouTube
  useEffect(() => {
    const id = youtubeId(form.youtube_url);
    const auto = id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
    setForm((f) => (f.thumbnail_url ? f : { ...f, thumbnail_url: auto }));
  }, [form.youtube_url]);

  const load = async () => {
    const data = await getAllEducation();
    const arr = Array.isArray(data) ? data : [];
    setItems(arr);
    if (arr.length > 0) setActive(arr[0]);
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!form.title || !form.youtube_url) return;
    const created = await createEducation(form);
    const targetCat = form.category || "Geral";
    const position = Number.isFinite(Number(form.order))
      ? Number(form.order)
      : 1;
    await placeItem(created, targetCat, position);
    setForm({
      title: "",
      youtube_url: "",
      description: "",
      category: "Geral",
      thumbnail_url: "",
      published: true,
      order: 1,
    });
  };

  const handleUpdate = async (item: any, updates: any) => {
    await updateEducation(item.id, { ...item, ...updates });
    await load();
  };

  const handleDelete = async (id: number) => {
    await deleteEducation(id);
    await load();
  };

  const categories = Array.from(
    new Set(items.map((it) => (it.category ? String(it.category) : "Geral")))
  ).sort((a, b) => a.localeCompare(b));

  const getByCategory = (cat: string) => {
    return items
      .filter((it) => (it.category ? String(it.category) : "Geral") === cat)
      .filter((it) => (heroItem ? it.id !== heroItem.id : true))
      .filter((it) =>
        searchText
          ? String(it.title || "")
              .toLowerCase()
              .includes(searchText.toLowerCase())
          : true
      )
      .sort((a, b) => {
        const oa = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
        const ob = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
        return oa - ob;
      });
  };

  const moveItem = async (item: any, delta: number) => {
    const isHero =
      Number.isFinite(Number(item.order)) && Number(item.order) === 0;
    if (isHero) return;
    const cat = item.category ? String(item.category) : "Geral";
    const list = items
      .filter((it) => (it.category ? String(it.category) : "Geral") === cat)
      .filter((it) => (heroItem ? it.id !== heroItem.id : true))
      .sort((a, b) => {
        const oa = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
        const ob = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
        return oa - ob;
      });
    const currentIndex = list.findIndex((x) => x.id === item.id);
    if (currentIndex < 0) return;
    const targetIndex = Math.max(
      0,
      Math.min(list.length - 1, currentIndex + delta)
    );
    if (targetIndex === currentIndex) return;
    const without = list.filter((x) => x.id !== item.id);
    const idx = Math.max(0, Math.min(without.length, targetIndex));
    const newList = [
      ...without.slice(0, idx),
      { ...item, category: cat },
      ...without.slice(idx),
    ];
    await resequenceCategory(cat, newList);
    await load();
  };

  const resequenceCategory = async (cat: string, list: any[]) => {
    await Promise.all(
      list.map((it, index) =>
        updateEducation(it.id, {
          ...it,
          category: cat,
          order: index + 1,
        })
      )
    );
  };
  const placeItem = async (item: any, targetCat: string, position: number) => {
    if (position === 0) {
      await setAsHero(item);
      return;
    }
    const sourceCat = item.category ? String(item.category) : "Geral";
    const baseList = items
      .filter(
        (it) => (it.category ? String(it.category) : "Geral") === targetCat
      )
      .filter((it) => (heroItem ? it.id !== heroItem.id : true))
      .filter((it) => it.id !== item.id)
      .sort((a, b) => {
        const oa = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
        const ob = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
        return oa - ob;
      });
    const idx = Math.max(0, Math.min(baseList.length, position - 1));
    const newList = [
      ...baseList.slice(0, idx),
      { ...item, category: targetCat },
      ...baseList.slice(idx),
    ];
    await resequenceCategory(targetCat, newList);
    if (sourceCat !== targetCat) {
      const sourceList = items
        .filter(
          (it) => (it.category ? String(it.category) : "Geral") === sourceCat
        )
        .filter((it) => (heroItem ? it.id !== heroItem.id : true))
        .filter((it) => it.id !== item.id)
        .sort((a, b) => {
          const oa = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
          const ob = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
          return oa - ob;
        });
      await resequenceCategory(sourceCat, sourceList);
    }
    await load();
  };

  const setAsHero = async (item: any) => {
    const cats = Array.from(
      new Set(items.map((it) => (it.category ? String(it.category) : "Geral")))
    );
    const updates: Promise<any>[] = [];
    updates.push(
      updateEducation(item.id, {
        ...item,
        order: 0,
      })
    );
    cats.forEach((cat) => {
      const list = items
        .filter(
          (it) =>
            (it.category ? String(it.category) : "Geral") === cat &&
            it.id !== item.id
        )
        .sort((a, b) => {
          const oa = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
          const ob = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
          return oa - ob;
        });
      list.forEach((it, index) => {
        updates.push(
          updateEducation(it.id, {
            ...it,
            order: index + 1,
          })
        );
      });
    });
    await Promise.all(updates);
    await load();
    setActive(item);
  };

  const heroItem =
    items.find(
      (it) => Number.isFinite(Number(it.order)) && Number(it.order) === 0
    ) || active;
  const heroId = heroItem ? youtubeId(heroItem.youtube_url) : "";
  const heroThumb = heroItem
    ? heroItem.thumbnail_url ||
      (heroId ? `https://img.youtube.com/vi/${heroId}/hqdefault.jpg` : "")
    : "";

  return (
    <Box className="min-h-screen h-auto overflow-y-scroll">
      <SectionTitle title="Área Educacional" />
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Adicione vídeos do YouTube com título, descrição, categoria e controle
        de publicação.
      </Typography>

      {heroItem && (
        <>
          <div className="md:hidden">
            <div className="w-full rounded-xl overflow-hidden bg-[#182133]">
              <div className="relative">
                {heroThumb ? (
                  <img
                    src={heroThumb}
                    alt={heroItem?.title}
                    className="w-full aspect-video object-cover"
                  />
                ) : (
                  <div className="w-full aspect-video bg-[#1f2530]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-[#24324D]/10 to-[#182133] z-10" />
              </div>
              <div className="p-3">
                <h1 className="text-lg font-bold tracking-tight text-white">
                  {heroItem?.title || "Área Educacional"}
                </h1>
                {heroItem?.description ? (
                  <p className="mt-2 text-white/90 text-xs leading-relaxed line-clamp-3">
                    {heroItem.description}
                  </p>
                ) : null}
                <div className="mt-2 inline-flex items-center gap-2">
                  <span className="rounded-full bg-white/10 border border-white/30 px-2.5 py-0.5 text-xs text-white">
                    {heroItem?.category || "Geral"}
                  </span>
                </div>
                {heroId ? (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      className="inline-flex items-center justify-center gap-2 bg-[#24324D] text-white font-semibold px-4 py-2 rounded-md shadow hover:opacity-95 w-full text-sm"
                      onClick={() => setVideoOpen(true)}
                    >
                      <span className="text-xs">▶</span>
                      Assistir agora
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="hidden md:block">
            <div className="relative h-[420px] bg-[#000000] rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-[#24324D]/10 to-[#182133] rounded-xl z-10" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#24324D] via-[#182133] to-transparent rounded-xl z-5" />
              {heroThumb && (
                <div className="absolute right-0 bottom-0 h-full w-1/2 rounded-r-xl overflow-hidden z-0">
                  <img
                    src={heroThumb}
                    alt={heroItem?.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="absolute inset-0 flex items-end z-20">
                <div className="px-6 pb-8 w-full">
                  <div className="max-w-4xl">
                    <h1 className="text-4xl font-bold tracking-tight text-white">
                      {heroItem?.title || "Área Educacional"}
                    </h1>
                    {heroItem?.description ? (
                      <p className="mt-3 text-white/90 text-lg max-w-3xl line-clamp-3">
                        {heroItem.description}
                      </p>
                    ) : null}
                    <div className="mt-3 inline-flex items-center gap-2">
                      <span className="rounded-full bg-white/10 border border-white/30 px-3 py-1 text-xs text-white">
                        {heroItem?.category || "Geral"}
                      </span>
                    </div>
                    {heroId ? (
                      <div className="mt-5 flex items-center gap-3">
                        <button
                          className="inline-flex items-center gap-2 bg-[#24324D] text-white font-semibold px-5 py-2.5 rounded-md shadow hover:opacity-95"
                          onClick={() => setVideoOpen(true)}
                        >
                          <span className="text-sm">▶</span>
                          Assistir agora
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <Paper sx={{ p: 2, mb: 3, mt: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              label="Buscar por título"
              size="small"
              fullWidth
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={adminButtonPrimarySx}
              onClick={() => setOpenCreate(true)}
            >
              Adicionar vídeo
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {categories.map((cat) => (
        <Box key={cat} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            {cat}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1 }}>
            {getByCategory(cat).map((item) => {
              const id = youtubeId(item.youtube_url);
              const thumb = (item.thumbnail_url ||
                (id
                  ? `https://img.youtube.com/vi/${id}/hqdefault.jpg`
                  : "")) as string;
              const order = Number.isFinite(Number(item.order))
                ? Number(item.order)
                : 0;
              return (
                <Paper
                  key={item.id}
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 2,
                    minWidth: 320,
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <Box
                      sx={{
                        position: "relative",
                        height: 140,
                        backgroundImage: thumb ? `url(${thumb})` : undefined,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    >
                      {id ? (
                        <button
                          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity"
                          onClick={() => {
                            setActive(item);
                            setVideoOpen(true);
                          }}
                        >
                          <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center">
                            <Play className="w-4 h-4 text-[#24324D]" />
                          </div>
                        </button>
                      ) : null}
                      {!item.published && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          Não publicado
                        </div>
                      )}
                    </Box>
                    <Box sx={{ p: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700 }}
                        noWrap
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {item.category || "Geral"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        Posição: {order}
                      </Typography>
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => moveItem(item, -1)}
                          disabled={order === 0}
                        >
                          <ArrowLeft className="w-4 h-4" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveItem(item, 1)}
                          disabled={order === 0}
                        >
                          <ArrowRight className="w-4 h-4" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setAsHero(item)}
                        >
                          <Star
                            className={`w-4 h-4 ${
                              order === 0
                                ? "text-yellow-500 fill-yellow-500"
                                : "text-gray-600"
                            }`}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditItem(item);
                            setOpenEdit(true);
                          }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleUpdate(item, { published: !item.published })
                          }
                          title={item.published ? "Despublicar" : "Publicar"}
                        >
                          {item.published ? (
                            <Eye className="w-4 h-4" />
                          ) : (
                            <EyeOff className="w-4 h-4" />
                          )}
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      ))}

      <ModalComponent
        open={videoOpen}
        handleClose={() => setVideoOpen(false)}
        title="Assistir vídeo"
        maxWidth={isMobile ? "92%" : 720}
        autoHeight
      >
        {heroId ? (
          <Box
            sx={{ position: "relative", width: "100%", aspectRatio: "16/9" }}
          >
            <iframe
              style={{ width: "100%", height: "100%" }}
              src={`https://www.youtube.com/embed/${heroId}?autoplay=1`}
              title={active?.title || "Vídeo"}
              frameBorder={0}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
        ) : null}
      </ModalComponent>

      <ModalComponent
        open={openCreate}
        handleClose={() => setOpenCreate(false)}
        title="Adicionar vídeo"
        maxWidth={isMobile ? "92%" : 640}
        autoHeight
      >
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Título"
                fullWidth
                size="small"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="YouTube URL"
                fullWidth
                size="small"
                value={form.youtube_url}
                onChange={(e) =>
                  setForm({ ...form, youtube_url: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrição"
                fullWidth
                size="small"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Categoria"
                fullWidth
                size="small"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Thumbnail URL (opcional)"
                fullWidth
                size="small"
                value={form.thumbnail_url}
                onChange={(e) =>
                  setForm({ ...form, thumbnail_url: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Switch
                checked={form.published}
                onChange={(e) =>
                  setForm({ ...form, published: e.target.checked })
                }
              />
            </Grid>
          </Grid>
        </Box>
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            sx={adminButtonOutlinedSx}
            onClick={() => setOpenCreate(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={adminButtonPrimarySx}
            onClick={handleCreate}
          >
            Salvar
          </Button>
        </Box>
      </ModalComponent>

      <ModalComponent
        open={openEdit}
        handleClose={() => setOpenEdit(false)}
        title="Editar vídeo"
        maxWidth={isMobile ? "92%" : 640}
        autoHeight
      >
        {editItem ? (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Título"
                  fullWidth
                  size="small"
                  value={editItem.title}
                  onChange={(e) =>
                    setEditItem({ ...editItem, title: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="YouTube URL"
                  fullWidth
                  size="small"
                  value={editItem.youtube_url}
                  onChange={(e) =>
                    setEditItem({ ...editItem, youtube_url: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descrição"
                  fullWidth
                  size="small"
                  value={editItem.description || ""}
                  onChange={(e) =>
                    setEditItem({ ...editItem, description: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Categoria"
                  fullWidth
                  size="small"
                  value={editItem.category || "Geral"}
                  onChange={(e) =>
                    setEditItem({ ...editItem, category: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Thumbnail URL (opcional)"
                  fullWidth
                  size="small"
                  value={editItem.thumbnail_url || ""}
                  onChange={(e) =>
                    setEditItem({
                      ...editItem,
                      thumbnail_url: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Switch
                  checked={!!editItem.published}
                  onChange={(e) =>
                    setEditItem({ ...editItem, published: e.target.checked })
                  }
                />
              </Grid>
            </Grid>
          </Box>
        ) : null}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            sx={adminButtonOutlinedSx}
            onClick={() => setOpenEdit(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            sx={adminButtonPrimarySx}
            onClick={async () => {
              if (!editItem) return;
              await handleUpdate(editItem, editItem);
              setOpenEdit(false);
            }}
          >
            Salvar
          </Button>
        </Box>
      </ModalComponent>

      <Box sx={{ mt: 3 }} />
    </Box>
  );
}
